<template>
  <PopupModalWithoutTransition
    :open="open"
    class="sm:max-w-3xl sm:w-full h-[540px]"
  >
    <template #header>
      <div class="flex items-stretch mb-4">
        <div class="flex flex-col justify-start text-left">
          <span class="text-display2">{{
            t("registration.opt.in.title")
          }}</span>
          <span class="text-display2 italic">{{
            t("registration.opt.in.sub.title")
          }}</span>
        </div>
      </div>
    </template>
    <template #body>
      <div>
        <h2>
          {{ t("registration.opt.in.body") }}
        </h2>
        <div class="pt-8 w-[380px] mx-auto">
          <div class="text-left mb-1">
            {{ t("registration.opt.in.verify.code.title") }}
          </div>
          <div class="flex gap-2 justify-center">
            <input
              v-for="(digit, idx) in codeInputs"
              :key="idx"
              ref="inputRefs"
              v-model="codeInputs[idx]"
              type="text"
              maxlength="1"
              class="w-14 h-14 placeholder-current placeholder-opacity-100 text-center border rounded border-light-gray focus:outline-none focus:border-bkk-100 focus:ring-bkk-100 focus:ring-1"
              placeholder="—"
              :aria-label="`Code digit ${idx + 1}`"
              autocomplete="one-time-code"
              @input="onInput(idx, $event)"
              @keydown.backspace="onBackspace(idx, $event)"
              @paste="idx === 0 ? onPaste($event) : null"
            />
          </div>
        </div>
        <div
          class="pt-4 w-[380px] mx-auto text-left underline cursor-pointer"
          @click="triggerResendCode()"
          @keydown.enter.prevent="triggerResendCode()"
        >
          {{ t("registration.opt.in.new.code.text.link") }}
        </div>
        <div v-if="error">{{ error }}</div>
      </div>
    </template>
    <template #footer>
      <div class="flex flex-col justify-center items-center mt-4 gap-4">
        <BaseButton
          variant="primary"
          :disabled="!isCodeComplete"
          @click="handleSubmit"
          >{{ t("welcome.proceed.button.caption") }}</BaseButton
        >
      </div>
    </template>
  </PopupModalWithoutTransition>
</template>

<script setup lang="ts">
import PopupModalWithoutTransition from "../layout/PopupModalWithoutTransition.vue";
import BaseButton from "../layout/button/BaseButton.vue";
import { ref, onMounted, nextTick, computed } from "vue";
import { useI18n } from "vue-i18n";
import { validateAppUser } from "../../../api/v2/register.ts";
import axios from "axios";
import { useUserStore } from "../../../store/v2/user";
import { resendCode } from "../../../api/v2/user.ts";
import { useNotificationStore } from "../../../store/v2/notification.js";

const notificationStore = useNotificationStore();
const { t } = useI18n();
const userStore = useUserStore();
const code = ref("");
const error = ref("");

const codeInputs = ref(Array(6).fill(""));
const inputRefs = ref([]);

onMounted(() => {
  inputRefs.value = inputRefs.value.slice(0, 6);
});

const isCodeComplete = computed(() =>
  codeInputs.value.every((c) => c.length === 1)
);

function onInput(idx: number, event: Event) {
  const value = (event.target as HTMLInputElement).value.replace(
    /[^0-9a-zA-Z]/,
    ""
  );
  codeInputs.value[idx] = value;
  if (value && idx < 5) {
    nextTick(() => {
      (inputRefs.value[idx + 1] as HTMLInputElement)?.focus();
    });
  }
}

function onBackspace(idx: number, event: KeyboardEvent) {
  if (!codeInputs.value[idx] && idx > 0) {
    nextTick(() => {
      (inputRefs.value[idx - 1] as HTMLInputElement)?.focus();
    });
  }
}

async function handleSubmit() {
  try {
    const code = codeInputs.value.join("");

    const response = await validateAppUser(code, userStore.externalUserId);
    if (response.status === 200) {
      emit("isVerified");
    } else {
      error.value =
        "Etwas ist schiefgelaufen. Bitte versuche es später erneut.";
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 404) {
          error.value =
            "Etwas ist schiefgelaufen. Bitte versuche es später erneut.";
        }
      } else {
        error.value =
          "Etwas ist schiefgelaufen. Bitte versuche es später erneut.";
      }
    }
  }
}

function onPaste(event: ClipboardEvent) {
  const pasted = (event.clipboardData?.getData("text") || "").replace(
    /[^0-9a-zA-Z]/g,
    ""
  );
  if (pasted.length > 1) {
    event.preventDefault();
    for (let i = 0; i < 6; i++) {
      codeInputs.value[i] = pasted[i] || "";
    }
    nextTick(() => {
      const lastIdx = Math.min(pasted.length, 6) - 1;
      (inputRefs.value[lastIdx] as HTMLInputElement)?.focus();
    });
  }
}

defineProps({
  open: Boolean,
});

const emit = defineEmits(["isVerified"]);

const canResend = ref(true);

async function triggerResendCode() {
  if (!canResend.value) {
    return;
  }

  canResend.value = false;

  try {
    const response = await resendCode();
    if (response.status === 200) {
      notificationStore.addNotification(
        Math.random(),
        null,
        t("registration.opt.in.verify.code.title"),
        t("opt.in.screen.resend.success"),
        false
      );
    } else {
      notificationStore.addNotification(
        Math.random(),
        null,
        t("registration.opt.in.verify.code.title"),
        t("opt.in.screen.resend.failure"),
        true
      );
    }
  } catch {
    notificationStore.addNotification(
      Math.random(),
      null,
      t("registration.opt.in.verify.code.title"),
      t("opt.in.screen.resend.failure"),
      true
    );
  } finally {
    setTimeout(() => {
      canResend.value = true;
    }, 3000);
  }
}
</script>
